import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container } from "reactstrap";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import useStoreFilters from "customHooks/useStoreFilters";
import { FILTER_PAGE } from "utils/constants";
import { stuckMetersListColumns } from "pages/meter-management/stuckMeters/StuckMetersListColumns";
import StuckMetersSearch from "pages/meter-management/stuckMeters/StuckMetersSearch";
import { retrieveMeters, unmountMeters } from "utils/api";
import { isEmpty } from "lodash";
import CustomButton from "components/Common/buttons/CustomButton";
import unmountIcon from "assets/images/svg/Maintenance.svg";
import NGITooltip from "components/Common/utils/NGITooltip";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import BulkUnmountMeterModal from "pages/meter-management/stuckMeters/unmountModal";
import wrenchIcon from "assets/images/svg/Wrench.svg";

const StuckMetersList = () => {
  const [isLoading, setLoader] = useState(false);
  const [list, setList] = useState(null);
  const { filters, setFilters } = useStoreFilters(FILTER_PAGE.STUCK_METERS);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [unmountId, setUnmountId] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isBulkUnmountOpen, setIsBulkUnmountOpen] = useState(false);

  useEffect(() => {
    !isEmpty(filters) ? handleSearch(filters) : handleSearch();
  }, []);

  const onListLoaded = useCallback(res => {
    const {
      result: { items },
    } = res?.data;
    if (items?.length === 0) {
      warnMessage("Stuck meters were not found");
    }
    setList(res?.data || null);
    setLoader(false);
  }, []);

  const handleSearch = useCallback((filterOptions = {}) => {
    setLoader(true);
    const filters = { ...filterOptions };
    setFilters(filters);
    retrieveMeters({ ...filters, condition: "Stuck" })
      .then(res => onListLoaded(res))
      .catch(err => {
        setLoader(false);
        setList(null);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const onUnmount = useCallback((ids = []) => {
    closeConfirmationModal();
    setLoader(true);
    unmountMeters(ids)
      .then(() => {
        successMessage("Selected meter(s) unmounted from cylinder successfully");
        handleSearch();
      })
      .catch(err => {
        setLoader(false);
        setList(null);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const rowSelection = {
    setSelectedRowKeys,
    onChange: rows => setSelectedRowKeys(rows),
    renderCell: (checked, record, index, node) => {
      if (selectedRowKeys.length === 10) {
        return <NGITooltip title="Only 10 items are allowed.">{node}</NGITooltip>;
      }
      return node;
    },
    hideSelectAll: true,
    getCheckboxProps: record => {
      const checkboxProps = {};
      if (selectedRowKeys.length === 10 && !selectedRowKeys.includes(record.id)) {
        checkboxProps.disabled = true;
      }
      return checkboxProps;
    },
  };

  const closeBulkRemove = useCallback(() => setIsBulkUnmountOpen(false), []);
  const openBulkRemove = useCallback(() => setIsBulkUnmountOpen(true), []);

  const openConfirmationModal = useCallback(id => {
    setUnmountId(id);
    setIsConfirmationOpen(true);
  }, []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const columns = useMemo(() => stuckMetersListColumns(openConfirmationModal), []);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className="filters-wrapper">
            <div className="filter-buttons">
              <CustomButton iconImg={wrenchIcon} onClick={openBulkRemove} type="primary" text="BULK UNMOUNT" />
              {!!selectedRowKeys.length && (
                <CustomButton
                  onClick={() => openConfirmationModal(selectedRowKeys)}
                  isButtonRound
                  iconImg={unmountIcon}
                  type="primary"
                />
              )}
            </div>

            <StuckMetersSearch handleSearch={handleSearch} />
          </div>
          <Col lg={12}>
            <NGITable
              filtersType={FILTER_PAGE.STUCK_METERS}
              isLoading={isLoading}
              rowSelection={rowSelection}
              rowKey={record => record.id}
              columns={columns}
              data={list}
              updateList={handleSearch}
            />
          </Col>

          {isConfirmationOpen && (
            <ConfirmationModal
              description={"Are you sure the selected meter(s) are unmounted from the cylinder?"}
              onCancel={closeConfirmationModal}
              onConfirm={() => {
                onUnmount(unmountId);
              }}
            />
          )}
          {isBulkUnmountOpen && <BulkUnmountMeterModal onClose={closeBulkRemove} />}
        </Container>
      </div>
    </>
  );
};

export default StuckMetersList;
